import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewRef
} from '@angular/core';
import { IProductListingConfig, ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import {
    IRelatedProductsEngineHandler,
    IRelatedProductsOptions,
    RelatedProductsService
} from '@df/catalog-ui/related-products/related-products.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { IListingSetup } from '../listing/listing';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class RelatedProductsComponentDef<T extends Product<IProductDataV2> = Product<IProductDataV2>>
    implements IRelatedProductsEngineHandler<T>, OnChanges, OnInit, OnDestroy
{
    protected subscriptions = new Subscription();

    /**
     * Product id or ids, product model or product model or data collection
     * We will find related products to these items
     *
     * Special use: string 'basket' will result basket products ids
     *
     */
    @Input()
    source?: number | number[] | Product | Product[] | 'basket';

    /**
     * Options passed to relatedProducts service
     *
     * IMPORTANT: options are passed to engine function so you can put there
     * anything that is needed by that engine (like pio)
     *
     * @see related-products.service.js:get
     */
    @Input({
        required: true
    })
    options!: IRelatedProductsOptions;

    /**
     * Min number of items required to print it
     */
    @Input()
    min = 0;

    /**
     * Items limit
     */
    @Input({
        transform: (value: number | undefined) => {
            return typeof value === 'undefined' ? 4 : value;
        }
    })
    limit = 4;

    /**
     * Loaded models verbosity
     */
    protected verbosity = 2;

    /**
     * Listing model
     */
    listing: ProductListing<T> | undefined;

    /**
     * Listing model subscription
     */
    private listingSubscription?: Subscription;

    /**
     * Subscribe to it if you need to know if component has models to print
     */
    @Output()
    isPrinted = new BehaviorSubject<boolean>(false);

    /**
     * Buffer for component state to determine if reload is needed
     */
    private currentState: any;

    /**
     * If component has been initiated
     */
    private initiated = false;

    /**
     * Product ids we find related products to
     */
    protected sourceProductIds: number[] = [];

    /**
     * Listing creation
     */
    readonly $listing = new Subject<ProductListing<T> | undefined>();

    private basketSubscription?: Subscription;

    /**
     * Change localy to always force update
     */
    forceUpdate = false;

    @NgInject(Events)
    protected events!: Events;

    /**
     * Subscription on engine data updates (for example recently viewed chagned and we need to update related products)
     */
    private relatedProductsSubscription?: Subscription;

    /**
     * class constructor
     */
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private customer: Customer,
        readonly client: Client,
        private relatedProductsService: RelatedProductsService,
        protected timeoutService: TimeoutService,
        private productListingFactory: ProductListingFactory,
        private productResource: ProductResource,
        readonly elementRef: ElementRef
    ) {
        this.changeDetectorRef.detach();
    }

    async ngOnInit() {
        this.initiated = true;
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.detectChanges()));

        // load customer for basket source
        if (this.source === 'basket') {
            await this.customer.model.load();
        }

        this.update();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['limit']) {
            this.listing?.setLimit(this.limit);
        }

        if (changes['source']) {
            this.basketSubscription?.unsubscribe();
            if (this.source === 'basket') {
                this.setSourceProductIds(this.customer.basket.getProductIdsToLoad());
                this.basketSubscription = this.customer.basket.subscribe(() =>
                    this.setSourceProductIds(this.customer.basket.getProductIdsToLoad())
                );
            } else {
                const models = Array.isArray(this.source) ? this.source : [this.source];
                this.setSourceProductIds(models.map(i => (typeof i === 'object' ? i.id : i)).filter(i => !!i) as number[]);
            }
        }

        if (changes['options']) {
            this.relatedProductsSubscription?.unsubscribe();
            if (this.options) {
                this.relatedProductsSubscription = this.relatedProductsService.subscribe(this.options, () => {
                    this.update(true);
                });
            }
            this.update();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.basketSubscription?.unsubscribe();
        this.listingSubscription?.unsubscribe();
        this.relatedProductsSubscription?.unsubscribe();
        if (this.isPrinted.value) {
            this.isPrinted.next(false);
        }
    }

    canPrint(): boolean {
        return this.isPrinted.getValue();
    }

    /**
     * Updates related products list
     */
    protected update(force = this.forceUpdate) {
        if (this.initiated) {
            const currentState = [this.sourceProductIds, this.options];
            if (!isEqual(currentState, this.currentState) || force) {
                this.currentState = currentState;
                this.relatedProductsService.get(
                    this.sourceProductIds,
                    this as IRelatedProductsEngineHandler<Product>,
                    this.options,
                    (relatedProductsIds: number[]) => {
                        if (!relatedProductsIds.length) {
                            this.setListing(undefined);
                        } else {
                            this.productResource.loadAllIds(relatedProductsIds, this.verbosity, { safe: true }).then(models => {
                                if (isEqual(currentState, this.currentState)) {
                                    this.setListing(
                                        this.productListingFactory.create<T>(
                                            this.getListingOptions(),
                                            models as T[],
                                            this.getProductListingSetup()
                                        )
                                    );
                                }
                            });
                        }
                    }
                );
            }
        }
    }

    private setSourceProductIds(ids: number[]) {
        this.sourceProductIds = ids;
        this.update();
    }

    /**
     * Update listing
     */
    private setListing(listing: ProductListing<T> | undefined) {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
            this.listingSubscription = undefined;
        }

        this.listing = listing;

        if (listing) {
            listing.setLimit(this.limit);
            this.listingSubscription = listing.subscribe(() => this.onModelsChange());
        }
        this.$listing.next(listing);
        this.onModelsChange();
    }

    protected onModelsChange() {
        const isValid = this.isListingValid();

        if (isValid !== this.isPrinted.getValue()) {
            this.isPrinted.next(isValid);
        }

        this.detectChanges();
    }

    private isListingValid(): boolean {
        return (this.listing?.models?.length || 0) > (this.min || 0);
    }

    protected getListingOptions(): Partial<IProductListingConfig> {
        return {
            type: 'product',
            routableCheck: !this.options || !this.options.nonRoutable,
            noLimitReset: true
        };
    }

    protected getProductListingSetup(): IListingSetup<T> | undefined {
        return undefined;
    }

    protected detectChanges(): void {
        this.timeoutService.async(() => {
            if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        });
    }
}
